import React, { useEffect, useState } from 'react';
import { Col, Row, Button, Container } from 'reactstrap';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Link } from 'react-router-dom';
import renderField from '../../../shared/components/custom/Field';
import t, { partial } from '../../../util/translation/translation';
import validateMessageTemplate from './validateMessageTemplate';
import { TargetAudience } from './types';
import RenderSelectField from '../../../shared/components/form/Select';
import renderRadioButtonField from '../../../shared/components/form/RadioButton';
import { toast } from 'react-toastify';
import Api from '../../../util/api';
import { connect } from 'react-redux';

const MessageTemplateForm = ({
  template,
  handleSubmit,
  initialize,
  formValues = {},
  change,
}) => {
  const p = partial('MessagesForm');
  const s = partial('shared');
  const o = partial('OfferForm');
  const [groups, setGroups] = useState(null);
  const [targetAudience, setTargetAudience] = useState(TargetAudience.all);
  const [levels, setLevels] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [segments, setSegments] = useState(null);
  const screens = [
    {
      label: p('homeScreen'),
      value: '/home/false',
      values: [{ label: p('homeScreen'), value: '-1' }],
    },
    {
      label: p('events'),
      value: '/more/eventStack/events',
      values: [{ label: `${p('frontpage')} ${p('events')}`, value: '-1' }],
    },
    {
      label: p('lotteries'),
      value: '/more/lotteryStack/lotteriesScreen',
      values: [{ label: `${p('frontpage')} ${p('lotteries')}`, value: '-1' }],
    },
    {
      label: p('auctions'),
      value: '/more/auctions/Auctions',
      values: [{ label: `${p('frontpage')} ${p('auctions')}`, value: '-1' }],
    },
    {
      label: p('news'),
      value: '/home/news/false',
      values: [{ label: `${p('frontpage')} ${p('news')}`, value: '-1' }],
    },
  ];
  const getData = async () => {
    try {
      const [resLevels, resGroups, resTiers, resSegments] = await Promise.all([
        Api.club.getLevels(),
        Api.club.getGroups(),
        Api.tiers.allTiers(),
        Api.spotlight.getSegments(),
      ]);
      setLevels([
        {
          label: 'Ignore',
          value: null,
        },
        {
          label: s('all'),
          value: [],
        },
        ...resLevels.map((level) => {
          const { id, value } = level;
          return {
            value: id,
            label: value,
          };
        }),
      ]);
      setGroups([
        {
          label: 'Ignore',
          value: null,
        },
        {
          label: s('all'),
          value: [],
        },
        ...resGroups.map((group) => {
          const { id, value } = group;
          return {
            value: id,
            label: value,
          };
        }),
      ]);

      setTiers([
        {
          label: 'Ignore',
          value: null,
        },
        {
          label: s('all'),
          value: [],
        },
        ...resTiers.data.map((tier) => ({
          label: tier.name,
          value: tier.id,
        })),
      ]);

      setSegments([
        {
          label: 'Ignore',
          value: null,
        },
        {
          label: s('all'),
          value: [],
        },
        ...resSegments.map((segment) => ({
          value: segment.id,
          label: segment.name,
        })),
      ]);
    } catch (err) {
      toast.error(err);
    }
  };
  useEffect(() => {
    getData();
  }, []);
  useEffect(() => {
    if (template && groups && levels && tiers && segments) {
      setTargetAudience(template.targetAudience.targetAudience || 'all');
      initialize({
        title: template.title,
        text: template.text,
        url: template.url,
        landingPageCategory: template?.landingPageCategory || screens[0],
        targetAudience: template.targetAudience.targetAudience,
        groups:
          template.targetAudience.groups.length > 0
            ? template.targetAudience.groups.map((groupId) => {
                return {
                  label:
                    groupId === 'all groups'
                      ? s('all')
                      : groups.find((group) => group.value === groupId)?.label,
                  value: groupId === 'all groups' ? [] : groupId,
                };
              })
            : [
                {
                  label: 'Ignore',
                  value: null,
                },
              ],
        levels:
          template.targetAudience.levels.length > 0
            ? template.targetAudience.levels.map((levelId) => {
                return {
                  label:
                    levelId === 'all levels'
                      ? s('all')
                      : levels.find((level) => level.value === levelId)?.label,
                  value: levelId === 'all levels' ? [] : levelId,
                };
              })
            : [
                {
                  label: 'Ignore',
                  value: null,
                },
              ],
        tiers:
          template.targetAudience.tiers.length > 0
            ? template.targetAudience.tiers.map((tierId) => {
                return {
                  label:
                    tierId === 'all'
                      ? s('all')
                      : tiers.find((tier) => tier.value === tierId)?.label,
                  value: tierId === 'all' ? [] : tierId,
                };
              })
            : [
                {
                  label: 'Ignore',
                  value: null,
                },
              ],
        segments:
          template.targetAudience.segments.length > 0
            ? template.targetAudience.segments.map((segmentId) => {
                return {
                  label:
                    segmentId === 'all segments'
                      ? s('all')
                      : segments.find((segment) => segment.value === segmentId)
                          ?.label,
                  value: segmentId === 'all segments' ? [] : segmentId,
                };
              })
            : [
                {
                  label: 'Ignore',
                  value: null,
                },
              ],
      });
    }
  }, [template, groups, levels, tiers, segments]);
  const adjustDropdowns = (fieldName) => {
    if (formValues[fieldName] && formValues[fieldName].length > 1) {
      const [old, ...newOnes] = formValues[fieldName];
      const newestOne = formValues[fieldName][formValues[fieldName].length - 1];
      if (old.value === null || Array.isArray(old.value)) {
        change(fieldName, newOnes);
      }
      if (newestOne.value === null || Array.isArray(newestOne.value)) {
        change(fieldName, [newestOne]);
      }
    }
  };
  useEffect(() => {
    adjustDropdowns('groups');
    adjustDropdowns('levels');
    adjustDropdowns('tiers');
    adjustDropdowns('segments');
  }, [
    formValues.levels,
    formValues.groups,
    formValues.tiers,
    formValues.segments,
  ]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Container>
        <Row>
          <Col md={6}>
            <div className="form__form-group">
              <span className="form-header">{s('title')}*</span>
              <Field
                name="title"
                component={renderField}
                placeholder={s('title')}
              />
            </div>
          </Col>
          <Col md={6}>
            <div className="form__form-group">
              <span className="form-header">{s('message')}*</span>
              <Field
                name="text"
                component={renderField}
                placeholder={s('message')}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div className="form__form-group">
              <span className="form-header">{p('addExternalUrl')}*</span>
              <Field
                name="url"
                component={renderField}
                placeholder={p('externalUrlPlaceholder')}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div className="flex">
              <span className="form mr-1">{o('targetAudience')}</span>
            </div>
            <div className="radio-horizontal">
              <div className="form__form-group margin-0">
                <div className="form__form-group-field">
                  <Field
                    name="targetAudience"
                    component={renderRadioButtonField}
                    label={s('all')}
                    radioValue={TargetAudience.all}
                    onChange={(e) => setTargetAudience(e)}
                  />
                </div>
              </div>
              <div className="form__form-group margin-0">
                <div className="form__form-group-field">
                  <Field
                    name="targetAudience"
                    component={renderRadioButtonField}
                    label={s('custom')}
                    radioValue="custom"
                    onChange={(e) => setTargetAudience(e)}
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <div className="form__form-group">
          {targetAudience === 'custom' && (
            <>
              <Row>
                <Col md={12}>
                  <div className="form__form-group">
                    <span className="form-header">
                      {t('HighlightBannerForm.selectSegments')}
                    </span>
                    <Field
                      name="segments"
                      placeholder={t('HighlightBannerForm.selectSegments')}
                      options={segments}
                      component={RenderSelectField}
                      multiple
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="form__form-group">
                    <span className="form-header">{o('selectLevels')}</span>
                    <Field
                      name="levels"
                      placeholder={o('selectLevels')}
                      options={levels}
                      component={RenderSelectField}
                      multiple
                    />
                  </div>
                  <div className="form__form-group">
                    <span className="form-header">{p('chooseGroup')}</span>
                    <Field
                      name="groups"
                      placeholder={p('chooseGroup')}
                      options={groups}
                      component={RenderSelectField}
                      multiple
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="form__form-group">
                    <span className="form-header">Choose a tier</span>
                    <Field
                      name="tiers"
                      placeholder="Choose a tier"
                      options={tiers}
                      component={RenderSelectField}
                      multiple
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
        <Row>
          <Col md={6}>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {s('categoryLanding')}
              </span>
              <div className="form__form-group-field d-flex">
                <Field
                  name="landingPageCategory"
                  component={RenderSelectField}
                  placeholder={p('chooseScreen')}
                  options={screens}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Col>
        <Button color="primary">{s('save')}</Button>
        <Link to={`/messages?tab=4`}>
          <Button>{s('close')}</Button>
        </Link>
      </Col>
    </form>
  );
};
const mapStateToProps = (state) => ({
  formValues: getFormValues('message_template_form')(state),
});

export default reduxForm({
  form: 'message_template_form',
  validate: validateMessageTemplate,
})(connect(mapStateToProps)(MessageTemplateForm));
